import { Building2, Home, Monitor, UserRound } from "lucide-react";

import type { CourtComplexRow, CourtRoomRow, EstablishmentRow } from "./types";
import {
  BackButton,
  DataTable,
  MobileInfoRow,
  OrangeIconBox,
  PrimaryButton,
  RowActions,
  StatusBadge,
  rowClassName,
} from "./DistrictCourtUi";
import { MeetingDetails } from "../highcourt/SharedUi";

interface DistrictCourtRoomsProps {
  title: string;
  complex: CourtComplexRow;
  establishment?: EstablishmentRow | null;
  rooms: CourtRoomRow[];
  isLoading: boolean;
  onBack: () => void;
  onAdd: () => void;
  onEdit: (room: CourtRoomRow) => void;
  onDelete: (room: CourtRoomRow) => void;
}

const getFloorLabel = (floor?: string) => {
  if (!floor) return "N/A";
  const numericFloor = Number(floor);

  if (Number.isNaN(numericFloor)) return floor;
  if (numericFloor === 0) return "Ground Floor";

  const suffixes = ["th", "st", "nd", "rd"];
  const value = numericFloor % 100;

  return `${numericFloor}${
    suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]
  } Floor`;
};

const judgeName = (judge: NonNullable<CourtRoomRow["judges"]>[number]) => {
  if (typeof judge === "string") return "";

  return (
    judge.label ||
    judge.judgeName ||
    judge.fullName ||
    [judge.firstName, judge.lastName].filter(Boolean).join(" ") ||
    judge.name ||
    "Assigned Judge"
  );
};

const establishmentType = (establishment?: EstablishmentRow | null) =>
  establishment?.type ||
  (establishment?.code?.includes("Court")
    ? establishment.code
    : "Chief Judicial Magistrate (CJM) Court");

const presidingOfficer = (establishment?: EstablishmentRow | null) =>
  establishment?.presidingOfficerName ||
  establishment?.presidingOfficer ||
  "No presiding officer assigned";

export const DistrictCourtRooms = ({
  title,
  complex,
  establishment,
  rooms,
  isLoading,
  onBack,
  onAdd,
  onEdit,
  onDelete,
}: DistrictCourtRoomsProps) => (
  <div>
    <BackButton label={`Back to ${complex.complexName}`} onClick={onBack} />
    <section className="mb-5 flex flex-col gap-5 rounded-xl border border-[#E8EDF5] bg-white px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center">
        <OrangeIconBox>
          <Home className="h-6 w-6" />
        </OrangeIconBox>
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-3">
            <h1 className="text-lg font-bold text-slate-950">{title}</h1>
            {establishment && (
              <span className="rounded-full border border-[#fcc09a] bg-[#fff4ee] px-2.5 py-1 text-xs font-semibold text-[#f26422]">
                {establishmentType(establishment)}
              </span>
            )}
            <StatusBadge status={establishment?.status || "Active"} />
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1">
              <UserRound className="h-3.5 w-3.5" />
              {presidingOfficer(establishment)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" />
              {complex.complexName}
            </span>
          </div>
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-[#E8EDF5]">
        <span className="inline-block px-8 py-4 text-center">
          <span className="block text-xl font-bold text-slate-950">
            {rooms.length}
          </span>
          <span className="block text-xs text-slate-400">Courtrooms</span>
        </span>
      </div>
    </section>

    <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-slate-500">
        Courtrooms under this establishment. Assign judges and configure
        facilities.
      </p>
      <PrimaryButton
        label="Add Courtroom"
        onClick={onAdd}
        className="w-full sm:w-auto"
      />
    </div>

    <DataTable
      headers={[
        "Hall Details",
        "Judge Count",
        "Judges Assigned",
        "Facilities",
        "Meeting Details",
        "Status",
        "Actions",
      ]}
      isLoading={isLoading}
      emptyLabel="No courtrooms yet"
      emptyAction="Add Courtroom"
      onEmptyAction={onAdd}
      mobileCards={rooms.map((room) => {
        const judgeLabels = (room.judges || []).map(judgeName).filter(Boolean);
        const hiddenJudgeCount = Math.max(judgeLabels.length - 2, 0);
        const hiddenFacilityCount = Math.max(
          (room.facilities?.length || 0) - 3,
          0,
        );

        return (
          <article
            key={room._id}
            className="rounded-xl border border-[#E8EDF5] bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-950">
                  {room.courtRoomName}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
                  <Monitor className="h-3.5 w-3.5" />
                  <span>{room.roomNumber}</span>
                  <span>·</span>
                  <span>{getFloorLabel(room.floorNumber)}</span>
                </div>
              </div>
              <RowActions
                onEdit={() => onEdit(room)}
                onDelete={() => onDelete(room)}
              />
            </div>
            <div className="mt-4 space-y-3">
              <MobileInfoRow
                label="Judge Count"
                value={room.seatingCapacity || judgeLabels.length || "N/A"}
              />
              <MobileInfoRow
                label="Judges Assigned"
                value={
                  <span className="flex flex-wrap justify-end gap-1.5">
                    {judgeLabels.length ? (
                      <>
                        {judgeLabels.slice(0, 2).map((judge) => (
                          <span
                            key={judge}
                            className="rounded-full bg-[#fde8d8] px-3 py-1 text-xs font-semibold text-[#c24e14]"
                          >
                            {judge}
                          </span>
                        ))}
                        {hiddenJudgeCount > 0 && (
                          <span className="rounded-full bg-[#fde8d8] px-3 py-1 text-xs font-semibold text-[#c24e14]">
                            +{hiddenJudgeCount}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">
                        {room.judges?.length || 0} judges assigned
                      </span>
                    )}
                  </span>
                }
              />
              <MobileInfoRow
                label="Facilities"
                value={
                  <span className="flex flex-wrap justify-end gap-1.5">
                    {room.facilities?.length ? (
                      <>
                        {room.facilities.slice(0, 3).map((facility) => (
                          <span
                            key={facility}
                            className="rounded-md border border-[#E2E8F0] bg-slate-50 px-2 py-1 text-xs text-slate-600"
                          >
                            {facility}
                          </span>
                        ))}
                        {hiddenFacilityCount > 0 && (
                          <span className="rounded-md border border-[#fcc09a] bg-[#fff4ee] px-2 py-1 text-xs font-semibold text-[#c24e14]">
                            +{hiddenFacilityCount}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </span>
                }
              />
              <MobileInfoRow
                label="Status"
                value={<StatusBadge status={room.status || "Active"} />}
              />
            </div>
          </article>
        );
      })}
    >
      {rooms.map((room) => {
        const judgeLabels = (room.judges || []).map(judgeName).filter(Boolean);
        const hiddenJudgeCount = Math.max(judgeLabels.length - 2, 0);
        const hiddenFacilityCount = Math.max(
          (room.facilities?.length || 0) - 3,
          0,
        );

        return (
          <tr key={room._id} className={rowClassName}>
            <td className="px-5 py-4">
              <div className="font-semibold text-slate-950">
                {room.courtRoomName}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
                <Monitor className="h-3.5 w-3.5" />
                <span>{room.roomNumber}</span>
                <span>·</span>
                <span>{getFloorLabel(room.floorNumber)}</span>
              </div>
            </td>
            <td className=" py-4 text-center items-center text-sm font-semibold text-slate-950">
              {room.seatingCapacity || judgeLabels.length || "N/A"}
            </td>
            <td className="px-5 py-4">
              <div className="flex flex-wrap gap-1.5">
                {judgeLabels.length ? (
                  <>
                    {judgeLabels.slice(0, 2).map((judge) => (
                      <span
                        key={judge}
                        className="rounded-full bg-[#fde8d8] px-3 py-1 text-xs font-semibold text-[#c24e14]"
                      >
                        {judge}
                      </span>
                    ))}
                    {hiddenJudgeCount > 0 && (
                      <span className="rounded-full bg-[#fde8d8] px-3 py-1 text-xs font-semibold text-[#c24e14]">
                        +{hiddenJudgeCount}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-xs text-slate-400">
                    {room.judges?.length || 0} judges assigned
                  </span>
                )}
              </div>
            </td>
            <td className="px-5 py-4">
              <div className="flex flex-wrap gap-1.5">
                {room.facilities?.length ? (
                  <>
                    {room.facilities.slice(0, 3).map((facility) => (
                      <span
                        key={facility}
                        className="rounded-md border border-[#E2E8F0] bg-slate-50 px-2 py-1 text-xs text-slate-600"
                      >
                        {facility}
                      </span>
                    ))}
                    {hiddenFacilityCount > 0 && (
                      <span className="rounded-md border border-[#fcc09a] bg-[#fff4ee] px-2 py-1 text-xs font-semibold text-[#c24e14]">
                        +{hiddenFacilityCount}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                )}
              </div>
            </td>
            <td className="px-5 py-4">
              <MeetingDetails
                meetingId={room?.meetingId}
                meetingPassword={room?.meetingPassword}
                meetingToken={room?.meetingToken}
              />
            </td>
            <td className="px-5 py-4">
              <StatusBadge status={room.status || "Active"} />
            </td>
            <td className="px-5 py-4">
              <RowActions
                onEdit={() => onEdit(room)}
                onDelete={() => onDelete(room)}
              />
            </td>
          </tr>
        );
      })}
    </DataTable>
  </div>
);
